import {
  useTaxCouponItemList,
  useTaxCouponItemRemove,
} from '@/core/domain/coupon/coupons/coupons.hooks';
import {
  TaxCouponItemTypeColor,
  TaxCouponItemTypeLabel,
  TaxCouponItem,
  TaxCouponItemType,
  TaxCouponStatus,
} from '@/core/domain/coupon/coupons/coupons.types';
import { Modal, Space, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { useInvoiceCFEFormContext } from '../InvoiceCFEFormProvider';
import InvoiceCFEItemModal from './CreateModal';
import { useDisclosure } from '@/hooks';
import { DeleteIcon, EditIcon } from '@/components/_common';
import { useState } from 'react';
import StatusTag from '@/components/_common/StatusTag';
import formatterPrice from '@/utils/number/formatterPrice';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';

const InvoiceCFeItems: React.FC = () => {
  const { taxCoupon } = useInvoiceCFEFormContext();

  const disabled =
    taxCoupon?.status !== TaxCouponStatus.TYPING ||
    !hasPermissions([Authorities.FISCAL_COUPON_EDIT]);

  const [itemSelected, setItemSelected] = useState<TaxCouponItem>();

  const [modalOpen, modalAction] = useDisclosure(false, {
    onClose: () => setItemSelected(undefined),
  });

  const taxCouponItems = useTaxCouponItemList(taxCoupon?.id);

  const taxCouponItemRemove = useTaxCouponItemRemove();

  function removeConfirm(item: TaxCouponItem) {
    Modal.confirm({
      title: 'Remover item do cupom',
      content: `Deseja realmente remover o item "${item.description}" do cupom fiscal?`,
      okText: 'Remover',
      okType: 'danger',
      onOk: async () => {
        if (taxCoupon) {
          await taxCouponItemRemove.mutateAsync({
            taxCouponItemId: item.id,
            taxCouponId: taxCoupon?.id,
          });
        }
      },
    });
  }

  const columns: ColumnsType<TaxCouponItem> = [
    {
      dataIndex: 'description',
      title: 'Descrição',
      width: 300,
    },
    {
      dataIndex: 'type',
      title: 'Tipo',
      render: (type: TaxCouponItemType) => (
        <StatusTag
          color={TaxCouponItemTypeColor[type]}
          label={TaxCouponItemTypeLabel[type]}
        />
      ),
    },
    {
      dataIndex: 'quantity',
      title: 'Qtde./Tempo',
      align: 'right',
      width: 100,
    },
    {
      dataIndex: 'unitMeasure',
      title: 'Unidade',
      width: 100,
    },
    {
      dataIndex: 'unitValue',
      title: 'Valor unitário (R$)',
      align: 'right',
      width: 200,
      render: (value) =>
        typeof value === 'number' ? formatterPrice(value) : '-',
    },
    {
      dataIndex: 'discount',
      title: 'Desconto (R$)',
      align: 'right',
      width: 200,
      render: (value) =>
        typeof value === 'number' ? formatterPrice(value) : '-',
    },
    {
      dataIndex: 'expenses',
      title: 'Despesa (R$)',
      align: 'right',
      width: 200,
      render: (value) =>
        typeof value === 'number' ? formatterPrice(value) : '-',
    },
    {
      title: 'Valor total (R$)',
      align: 'right',
      width: 200,
      render: (_, item) => (
        <strong>
          {formatterPrice(
            item.quantity * item.unitValue -
              (item.discount || 0) -
              (item.expenses || 0),
          )}
        </strong>
      ),
    },
    {
      title: 'Ação',
      align: 'right',
      fixed: 'right',
      render: (_, item) => (
        <Space size="middle">
          <EditIcon
            iconProps={{
              onClick: () => {
                setItemSelected(item);
                modalAction.open();
              },
            }}
            disabled={disabled}
          />

          <DeleteIcon onClick={() => removeConfirm(item)} disabled={disabled} />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        dataSource={taxCouponItems.data}
        loading={taxCouponItems.isLoading}
        columns={columns}
        scroll={{ x: true }}
        pagination={false}
        rowKey={(row) => row.itemId}
      />

      <InvoiceCFEItemModal
        open={modalOpen}
        onCancel={modalAction.close}
        item={itemSelected}
      />
    </>
  );
};

export default InvoiceCFeItems;
